import React, { useEffect, useState } from 'react'
import {
  Box,
  Button,
  Card,
  CardContent,
  Chip,
  Collapse,
  Divider,
  FormControl,
  Grid,
  InputLabel,
  MenuItem,
  Select,
  Stack,
  TextField,
  useMediaQuery,
  useTheme,
} from '@mui/material'
import {
  Clear as ClearIcon,
  ExpandLess as ExpandLessIcon,
  ExpandMore as ExpandMoreIcon,
} from '@mui/icons-material'
import { CollectionSearch } from '@/features/collection/components/CollectionSearch'
import { TOUCH_TARGET_MIN_PX } from '@components/layout/layoutConstants'

const STRENGTH_OPTIONS = ['Mild', 'Mild-Medium', 'Medium', 'Medium-Full', 'Full']
const WRAPPER_OPTIONS = ['Connecticut', 'Habano', 'Maduro', 'Corojo', 'Oscuro', 'Sun Grown', 'Cameroon', 'Sumatra']

export type FindFilterValues = {
  strength: string
  wrapperType: string
  origin: string
  vitolaId: string
  minPrice: string
  maxPrice: string
}

type FindFiltersPanelProps = {
  searchInput: string
  onSearchChange: (value: string) => void
  onSearchClear: () => void
  filters: FindFilterValues
  activeFilterCount: number
  onFilterChange: <K extends keyof FindFilterValues>(key: K, value: FindFilterValues[K]) => void
  onClearFilters: () => void
}

export const FindFiltersPanel: React.FC<FindFiltersPanelProps> = ({
  searchInput,
  onSearchChange,
  onSearchClear,
  filters,
  activeFilterCount,
  onFilterChange,
  onClearFilters,
}) => {
  const theme = useTheme()
  const isMdUp = useMediaQuery(theme.breakpoints.up('md'))
  const [filtersExpanded, setFiltersExpanded] = useState(false)

  useEffect(() => {
    if (isMdUp) {
      setFiltersExpanded(true)
    }
  }, [isMdUp])

  const showFilterToggle = !isMdUp

  return (
    <Card sx={{ mb: 2 }} data-testid="find-filters-panel">
      <CardContent>
        <Box mb={showFilterToggle || isMdUp ? 2 : 0}>
          <CollectionSearch
            value={searchInput}
            onChange={onSearchChange}
            onClear={onSearchClear}
            placeholder="Search by brand, cigar line, or description..."
          />
        </Box>

        {showFilterToggle && (
          <Button
            size="small"
            onClick={() => setFiltersExpanded((prev) => !prev)}
            endIcon={filtersExpanded ? <ExpandLessIcon /> : <ExpandMoreIcon />}
            sx={{ mb: filtersExpanded ? 2 : 1, minHeight: TOUCH_TARGET_MIN_PX }}
            aria-expanded={filtersExpanded}
          >
            {filtersExpanded ? 'Hide filters' : 'Show filters'}
            {activeFilterCount > 0 ? ` (${activeFilterCount})` : ''}
          </Button>
        )}

        <Collapse in={filtersExpanded || isMdUp} timeout="auto" unmountOnExit={false}>
          <Grid container spacing={2}>
            <Grid item xs={12} sm={6} md={4}>
              <FormControl fullWidth size="small">
                <InputLabel id="find-filter-strength-label">Strength</InputLabel>
                <Select
                  labelId="find-filter-strength-label"
                  label="Strength"
                  value={filters.strength}
                  onChange={(e) => onFilterChange('strength', e.target.value)}
                  inputProps={{ 'aria-label': 'Filter by strength' }}
                >
                  <MenuItem value="">All</MenuItem>
                  {STRENGTH_OPTIONS.map((s) => (
                    <MenuItem key={s} value={s}>
                      {s}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12} sm={6} md={4}>
              <FormControl fullWidth size="small">
                <InputLabel id="find-filter-wrapper-label">Wrapper Type</InputLabel>
                <Select
                  labelId="find-filter-wrapper-label"
                  label="Wrapper Type"
                  value={filters.wrapperType}
                  onChange={(e) => onFilterChange('wrapperType', e.target.value)}
                  inputProps={{ 'aria-label': 'Filter by wrapper type' }}
                >
                  <MenuItem value="">All</MenuItem>
                  {WRAPPER_OPTIONS.map((w) => (
                    <MenuItem key={w} value={w}>
                      {w}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12} sm={6} md={4}>
              <TextField
                fullWidth
                size="small"
                label="Origin Country"
                value={filters.origin}
                onChange={(e) => onFilterChange('origin', e.target.value)}
                placeholder="e.g. Nicaragua, Honduras"
                inputProps={{ 'aria-label': 'Filter by origin country' }}
              />
            </Grid>
            <Grid item xs={12} sm={6} md={4}>
              <TextField
                fullWidth
                size="small"
                label="Min Price"
                value={filters.minPrice}
                onChange={(e) => onFilterChange('minPrice', e.target.value)}
                placeholder="0.00"
                type="number"
                inputProps={{ min: 0, step: 0.01, 'aria-label': 'Filter by minimum price' }}
              />
            </Grid>
            <Grid item xs={12} sm={6} md={4}>
              <TextField
                fullWidth
                size="small"
                label="Max Price"
                value={filters.maxPrice}
                onChange={(e) => onFilterChange('maxPrice', e.target.value)}
                placeholder="999.99"
                type="number"
                inputProps={{ min: 0, step: 0.01, 'aria-label': 'Filter by maximum price' }}
              />
            </Grid>
            <Grid item xs={12} sm={6} md={4}>
              <TextField
                fullWidth
                size="small"
                label="Vitola"
                value={filters.vitolaId}
                onChange={(e) => onFilterChange('vitolaId', e.target.value)}
                placeholder="Vitola ID or search"
                inputProps={{ 'aria-label': 'Filter by vitola' }}
              />
            </Grid>
          </Grid>
        </Collapse>

        {activeFilterCount > 0 && (
          <>
            <Divider sx={{ my: 2 }} />
            <Box
              sx={{
                display: 'flex',
                alignItems: 'center',
                gap: 1,
                overflowX: 'auto',
                flexWrap: 'nowrap',
                pb: 0.5,
                mx: -0.5,
                px: 0.5,
                WebkitOverflowScrolling: 'touch',
              }}
              data-testid="find-active-filter-chips"
            >
              <Stack direction="row" spacing={1} useFlexGap sx={{ flexWrap: 'nowrap' }}>
                {filters.strength && (
                  <Chip
                    label={`Strength: ${filters.strength}`}
                    size="small"
                    onDelete={() => onFilterChange('strength', '')}
                    color="primary"
                    variant="outlined"
                    sx={{ flexShrink: 0 }}
                  />
                )}
                {filters.wrapperType && (
                  <Chip
                    label={`Wrapper: ${filters.wrapperType}`}
                    size="small"
                    onDelete={() => onFilterChange('wrapperType', '')}
                    color="primary"
                    variant="outlined"
                    sx={{ flexShrink: 0 }}
                  />
                )}
                {filters.origin && (
                  <Chip
                    label={`Origin: ${filters.origin}`}
                    size="small"
                    onDelete={() => onFilterChange('origin', '')}
                    color="primary"
                    variant="outlined"
                    sx={{ flexShrink: 0 }}
                  />
                )}
                {filters.vitolaId && (
                  <Chip
                    label={`Vitola: ${filters.vitolaId}`}
                    size="small"
                    onDelete={() => onFilterChange('vitolaId', '')}
                    color="primary"
                    variant="outlined"
                    sx={{ flexShrink: 0 }}
                  />
                )}
                {filters.minPrice && (
                  <Chip
                    label={`Min: $${filters.minPrice}`}
                    size="small"
                    onDelete={() => onFilterChange('minPrice', '')}
                    color="primary"
                    variant="outlined"
                    sx={{ flexShrink: 0 }}
                  />
                )}
                {filters.maxPrice && (
                  <Chip
                    label={`Max: $${filters.maxPrice}`}
                    size="small"
                    onDelete={() => onFilterChange('maxPrice', '')}
                    color="primary"
                    variant="outlined"
                    sx={{ flexShrink: 0 }}
                  />
                )}
                <Button
                  size="small"
                  startIcon={<ClearIcon />}
                  onClick={onClearFilters}
                  color="inherit"
                  sx={{ flexShrink: 0, minHeight: TOUCH_TARGET_MIN_PX }}
                >
                  Clear all
                </Button>
              </Stack>
            </Box>
          </>
        )}
      </CardContent>
    </Card>
  )
}
